/**
 * MinistrareIA - Onboarding Cards
 * Cards de categoria, tela de intro e tela de conclusão
 * 
 * UPDATED:
 * - CompleteCard com animações de loading progressivas
 * - Nome do agente editável (max 2 palavras)
 * - UI polida com transições
 */
import React, { useState, useEffect, createElement } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowRight, Check, Sparkles, Bot, MessageSquare, 
  Loader2, CheckCircle, Rocket, Play, Pencil, X, Save
} from 'lucide-react';

// ============================================
// INTRO CARD - Tela de boas-vindas
// ============================================

export const IntroCard = ({ onStart, userName }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100%',
      padding: '40px 24px',
      textAlign: 'center',
    },
    iconWrapper: {
      width: '80px',
      height: '80px',
      borderRadius: '24px',
      backgroundColor: `${colors.accentPrimary}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '32px',
      animation: 'introFloat 3s ease-in-out infinite',
    },
    title: {
      fontSize: '32px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '12px',
      lineHeight: 1.2,
    },
    titleHighlight: {
      background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, #34d399 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    subtitle: {
      fontSize: '16px',
      color: colors.textSecondary,
      marginBottom: '40px',
      maxWidth: '400px',
      lineHeight: 1.6,
    },
    features: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      marginBottom: '40px',
      width: '100%',
      maxWidth: '360px',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '14px 18px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`,
    },
    featureIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      backgroundColor: `${colors.accentPrimary}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.accentPrimary,
      flexShrink: 0,
    },
    featureText: {
      flex: 1,
      textAlign: 'left',
    },
    featureTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    featureDesc: {
      fontSize: '12px',
      color: colors.textSecondary,
    },
    startButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '16px 40px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    timeEstimate: {
      marginTop: '16px',
      fontSize: '13px',
      color: colors.textTertiary,
    },
  };

  const features = [
    { icon: Bot, title: 'Personalizado', desc: 'Agente único para seu negócio' },
    { icon: MessageSquare, title: 'Conversacional', desc: 'Criação guiada passo a passo' },
    { icon: Sparkles, title: 'Inteligente', desc: 'Aprende sobre sua empresa' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.iconWrapper}>
        <Rocket size={40} color={colors.accentPrimary} />
      </div>

      <h1 style={styles.title}>
        {userName ? `Olá, ${userName.split(' ')[0]}!` : 'Olá!'}<br />
        <span style={styles.titleHighlight}>Vamos criar seu agente</span>
      </h1>

      <p style={styles.subtitle}>
        Em poucos minutos você terá um assistente de IA personalizado 
        para atender seus clientes no WhatsApp.
      </p>

      <div style={styles.features}>
        {features.map((feature, index) => (
          <div 
            key={index} 
            style={{
              ...styles.featureItem,
              animation: `introSlideIn 0.4s ease-out ${index * 0.1}s both`
            }}
          >
            <div style={styles.featureIcon}>
              {createElement(feature.icon, { size: 18 })}
            </div>
            <div style={styles.featureText}>
              <div style={styles.featureTitle}>{feature.title}</div>
              <div style={styles.featureDesc}>{feature.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <button 
        style={styles.startButton}
        onClick={onStart}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = `0 8px 24px ${colors.accentPrimary}40`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <Play size={20} />
        Começar
      </button>

      <p style={styles.timeEstimate}>⏱ Leva cerca de 3 minutos</p>

      <style>{`
        @keyframes introFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes introSlideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

// ============================================
// CATEGORY CARDS - Seleção de categoria
// ============================================

export const CategoryCards = ({ categories, onSelect }) => {
  const { colors, theme } = useTheme();
  const [selectedId, setSelectedId] = useState(null);
  const isDark = theme === 'dark';

  const handleSelect = (category) => {
    setSelectedId(category.id);
    setTimeout(() => onSelect(category), 300);
  };

  const styles = {
    container: {
      padding: '24px',
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '15px',
      color: colors.textSecondary,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
      maxWidth: '500px',
      margin: '0 auto',
    },
    card: (isSelected, categoryColor) => ({
      padding: '24px 20px',
      backgroundColor: isSelected 
        ? colors.accentPrimary 
        : (isDark ? colors.bgPrimary : '#ffffff'),
      border: `2px solid ${isSelected ? colors.accentPrimary : colors.borderColor}`,
      borderRadius: '16px',
      cursor: selectedId ? 'default' : 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      opacity: selectedId && !isSelected ? 0.5 : 1,
    }),
    cardIcon: (isSelected, categoryColor) => ({
      width: '56px',
      height: '56px',
      borderRadius: '16px',
      backgroundColor: isSelected 
        ? 'rgba(255,255,255,0.2)' 
        : `${categoryColor}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '16px',
      color: isSelected ? 'white' : categoryColor,
    }),
    cardLabel: (isSelected) => ({
      fontSize: '15px',
      fontWeight: 600,
      color: isSelected ? 'white' : colors.textPrimary,
      marginBottom: '4px',
    }),
    cardDesc: (isSelected) => ({
      fontSize: '12px',
      color: isSelected ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
    }),
    checkIndicator: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Qual é o seu segmento?</h2>
        <p style={styles.subtitle}>Escolha a categoria que melhor representa seu negócio</p>
      </div>

      <div style={styles.grid}>
        {categories.map((category, index) => {
          const isSelected = selectedId === category.id;
          const IconComponent = category.icon;

          return (
            <div
              key={category.id}
              style={{
                ...styles.card(isSelected, category.color),
                position: 'relative',
                animation: `cardFadeIn 0.3s ease-out ${index * 0.1}s both`,
              }}
              onClick={() => !selectedId && handleSelect(category)}
              onMouseEnter={(e) => {
                if (!selectedId) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = colors.shadowLg;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {isSelected && (
                <div style={styles.checkIndicator}>
                  <Check size={14} color={colors.accentPrimary} strokeWidth={3} />
                </div>
              )}
              
              <div style={styles.cardIcon(isSelected, category.color)}>
                {IconComponent && <IconComponent size={28} />}
              </div>
              
              <div style={styles.cardLabel(isSelected)}>{category.label}</div>
              <div style={styles.cardDesc(isSelected)}>{category.description}</div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// ============================================
// COMPLETE CARD - Tela de conclusão
// ============================================

// Estados de loading
const LOADING_STAGES = [
  { text: 'Analisando suas respostas...', duration: 1500 },
  { text: 'Aprimorando instruções...', duration: 2000 },
  { text: 'Configurando personalidade...', duration: 1500 },
  { text: 'Finalizando seu agente...', duration: 1000 }
];

export const CompleteCard = ({ 
  onGoToInbox, 
  onConfigureMore, 
  agentName: initialAgentName, 
  onAgentNameChange,
  isLoading = false,
  loadingStage = 0 
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Estado local para nome editável
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(initialAgentName || '');
  const [nameError, setNameError] = useState('');

  // Atualizar quando o nome inicial mudar
  useEffect(() => {
    setEditedName(initialAgentName || '');
  }, [initialAgentName]);

  // Validar nome (max 2 palavras)
  const validateName = (name) => {
    const words = name.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length > 2) {
      return 'Máximo 2 palavras';
    }
    if (name.trim().length < 2) {
      return 'Nome muito curto';
    }
    if (name.trim().length > 20) {
      return 'Nome muito longo';
    }
    return '';
  };

  const handleSaveName = () => {
    const error = validateName(editedName);
    if (error) {
      setNameError(error);
      return;
    }
    setNameError('');
    setIsEditing(false);
    if (onAgentNameChange) {
      onAgentNameChange(editedName.trim());
    }
  };

  const handleCancelEdit = () => {
    setEditedName(initialAgentName || '');
    setNameError('');
    setIsEditing(false);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setEditedName(value);
    setNameError(validateName(value));
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100%',
      padding: '40px 24px',
      textAlign: 'center',
    },
    // Loading states
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '32px',
    },
    loadingSpinner: {
      position: 'relative',
      width: '100px',
      height: '100px',
    },
    spinnerOuter: {
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      border: `4px solid ${colors.borderColor}`,
      borderTopColor: colors.accentPrimary,
      animation: 'spin 1s linear infinite',
    },
    spinnerInner: {
      position: 'absolute',
      inset: '15px',
      borderRadius: '50%',
      border: `4px solid ${colors.borderColor}`,
      borderBottomColor: '#34d399',
      animation: 'spin 1.5s linear infinite reverse',
    },
    spinnerCenter: {
      position: 'absolute',
      inset: '30px',
      borderRadius: '50%',
      backgroundColor: `${colors.accentPrimary}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    loadingText: {
      fontSize: '16px',
      fontWeight: 500,
      color: colors.textSecondary,
      animation: 'pulse 2s ease-in-out infinite',
    },
    loadingSteps: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      width: '100%',
      maxWidth: '300px',
    },
    loadingStep: (isActive, isComplete) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      backgroundColor: isComplete 
        ? `${colors.accentPrimary}10` 
        : (isDark ? colors.bgPrimary : '#ffffff'),
      border: `1px solid ${isActive ? colors.accentPrimary : colors.borderColor}`,
      borderRadius: '10px',
      opacity: isActive || isComplete ? 1 : 0.5,
      transition: 'all 0.3s ease',
    }),
    stepIcon: (isComplete) => ({
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: isComplete ? colors.accentPrimary : colors.borderColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }),
    stepText: {
      fontSize: '14px',
      color: colors.textPrimary,
      textAlign: 'left',
    },
    // Success state
    successIcon: {
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      backgroundColor: '#10B98120',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '32px',
      animation: 'completeScale 0.5s ease-out',
    },
    title: {
      fontSize: '28px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '12px',
    },
    subtitle: {
      fontSize: '16px',
      color: colors.textSecondary,
      marginBottom: '32px',
      maxWidth: '400px',
      lineHeight: 1.6,
    },
    agentPreview: {
      padding: '20px 24px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '16px',
      marginBottom: '32px',
      width: '100%',
      maxWidth: '400px',
    },
    agentHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    agentAvatar: {
      width: '56px',
      height: '56px',
      borderRadius: '14px',
      backgroundColor: `${colors.accentPrimary}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.accentPrimary,
      flexShrink: 0,
    },
    agentInfo: {
      flex: 1,
      textAlign: 'left',
    },
    agentNameContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '4px',
    },
    agentName: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
    },
    editButton: {
      width: '28px',
      height: '28px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
    },
    agentStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: '#10B981',
    },
    statusDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#10B981',
      animation: 'pulse 2s ease-in-out infinite',
    },
    // Edit mode
    editContainer: {
      marginTop: '16px',
      padding: '16px',
      backgroundColor: isDark ? colors.bgSecondary : '#f9fafb',
      borderRadius: '12px',
    },
    editLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: colors.textSecondary,
      marginBottom: '8px',
      textAlign: 'left',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    editInput: {
      width: '100%',
      padding: '12px 14px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `2px solid ${nameError ? '#ef4444' : colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: 600,
      color: colors.textPrimary,
      outline: 'none',
      textAlign: 'center',
      transition: 'border-color 0.2s ease',
    },
    editHint: {
      fontSize: '12px',
      color: nameError ? '#ef4444' : colors.textTertiary,
      marginTop: '8px',
      textAlign: 'center',
    },
    editActions: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px',
    },
    cancelBtn: {
      flex: 1,
      padding: '10px',
      backgroundColor: 'transparent',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px',
      color: colors.textSecondary,
      fontSize: '14px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
    },
    saveBtn: {
      flex: 1,
      padding: '10px',
      backgroundColor: nameError ? colors.borderColor : colors.accentPrimary,
      border: 'none',
      borderRadius: '8px',
      color: nameError ? colors.textTertiary : 'white',
      fontSize: '14px',
      fontWeight: 600,
      cursor: nameError ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
    },
    // Buttons
    buttonsWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      width: '100%',
      maxWidth: '400px',
    },
    primaryButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '16px 24px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
  };

  // Loading State
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          {/* Spinner animado */}
          <div style={styles.loadingSpinner}>
            <div style={styles.spinnerOuter} />
            <div style={styles.spinnerInner} />
            <div style={styles.spinnerCenter}>
              <Sparkles size={24} color={colors.accentPrimary} />
            </div>
          </div>

          {/* Texto de loading */}
          <p style={styles.loadingText}>
            {LOADING_STAGES[loadingStage]?.text || 'Processando...'}
          </p>

          {/* Steps de progresso */}
          <div style={styles.loadingSteps}>
            {LOADING_STAGES.map((stage, index) => {
              const isComplete = index < loadingStage;
              const isActive = index === loadingStage;
              
              return (
                <div key={index} style={styles.loadingStep(isActive, isComplete)}>
                  <div style={styles.stepIcon(isComplete)}>
                    {isComplete ? (
                      <Check size={14} color="white" strokeWidth={3} />
                    ) : isActive ? (
                      <Loader2 
                        size={14} 
                        color={colors.accentPrimary} 
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                    ) : (
                      <span style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        backgroundColor: colors.textTertiary 
                      }} />
                    )}
                  </div>
                  <span style={styles.stepText}>{stage.text.replace('...', '')}</span>
                </div>
              );
            })}
          </div>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        `}</style>
      </div>
    );
  }

  // Success State
  return (
    <div style={styles.container}>
      <div style={styles.successIcon}>
        <CheckCircle size={50} color="#10B981" />
      </div>

      <h1 style={styles.title}>Agente criado com sucesso! 🎉</h1>
      
      <p style={styles.subtitle}>
        Seu assistente de IA está pronto para atender seus clientes. 
        Conecte seu WhatsApp para começar a usar.
      </p>

      {/* Preview do Agente */}
      <div style={styles.agentPreview}>
        <div style={styles.agentHeader}>
          <div style={styles.agentAvatar}>
            <Bot size={28} />
          </div>
          <div style={styles.agentInfo}>
            <div style={styles.agentNameContainer}>
              <span style={styles.agentName}>{editedName || 'Meu Agente'}</span>
              {!isEditing && (
                <button
                  style={styles.editButton}
                  onClick={() => setIsEditing(true)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.accentPrimary}15`;
                    e.currentTarget.style.color = colors.accentPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.textTertiary;
                  }}
                >
                  <Pencil size={14} />
                </button>
              )}
            </div>
            <div style={styles.agentStatus}>
              <div style={styles.statusDot} />
              Pronto para atender
            </div>
          </div>
        </div>

        {/* Modo de edição */}
        {isEditing && (
          <div style={styles.editContainer}>
            <div style={styles.editLabel}>Nome do Agente</div>
            <input
              style={styles.editInput}
              value={editedName}
              onChange={handleNameChange}
              placeholder="Ex: Max Vendas"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') handleCancelEdit();
              }}
              onFocus={(e) => {
                if (!nameError) {
                  e.target.style.borderColor = colors.accentPrimary;
                }
              }}
              onBlur={(e) => {
                if (!nameError) {
                  e.target.style.borderColor = colors.borderColor;
                }
              }}
            />
            <div style={styles.editHint}>
              {nameError || 'Máximo 2 palavras • Aparecerá no seu workspace'}
            </div>
            <div style={styles.editActions}>
              <button style={styles.cancelBtn} onClick={handleCancelEdit}>
                <X size={14} />
                Cancelar
              </button>
              <button 
                style={styles.saveBtn} 
                onClick={handleSaveName}
                disabled={!!nameError}
              >
                <Save size={14} />
                Salvar
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={styles.buttonsWrapper}>
        <button 
          style={styles.primaryButton}
          onClick={onGoToInbox}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <MessageSquare size={20} />
          Ir para o Inbox
        </button>
      </div>

      <style>{`
        @keyframes completeScale {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default {
  IntroCard,
  CategoryCards,
  CompleteCard
};